import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import { toast } from "react-toastify";
import NoData from "../../loaders/NoData";
import NotificationSkeleton from "./NotificationSkeleton";
import { notificationLink } from "./AllNotifications";
import './Notifications.scss'

const UnreadNotifications = ({ allNotifications, setOpenNotificationPanel, getValue,
    setNumber, loading, onCleared }) => {

    const { fetchData } = useAxios();

    const navigate = useNavigate();

    const [unReadNotifications, setUnReadNotifications] = useState([]);

    const [clearing, setClearing] = useState(false);

    useEffect(() => {
        const unReadNotifies = allNotifications.filter(each => !each.receiver.read)
        setUnReadNotifications(unReadNotifies);
        /* the count of what was just worked out, not of what state still
           holds — reading it before the set left the header badge showing the
           previous list's length */
        getValue(unReadNotifies.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allNotifications]);

    const readNotificationUpdate = async (notification) => {
        const payload = { _id: notification._id };

        const resp = await fetchData({ url: `${URLS.updateNotificationReadStatus}`, method: 'post', body: payload });

        /* Prefer the link the server hands back, but fall back to the one
           already on the record — a failed read-update should not leave the
           click doing nothing at all. */
        const url = notificationLink(resp?.data) || notificationLink(notification);

        setOpenNotificationPanel(false);
        if (url) navigate(url);
    }

    /* Clears the whole unread list in one call. The notifications are not
       deleted — they stay in the All tab — they are marked read, which is
       what stops them coming back here. */
    const clearAll = async () => {
        if (clearing || !unReadNotifications.length) return;
        setClearing(true);

        const resp = await fetchData({
            url: URLS.clearUnreadNotifications, method: 'post', body: {},
        });

        /* Only a 200 is a success, the way the rest of this codebase reads a
           response. Checking `success === false` was not enough: a 404 comes
           back as HTML, which useAxios cannot parse into an envelope, so a
           missing route would have been treated as a clear that worked. */
        if (resp?.status !== 200) {
            setClearing(false);
            /* the status is carried through, so a 404 (the server not yet
               restarted onto this route) is distinguishable from a real
               failure instead of both reading as "something went wrong" */
            const why = resp?.status ? ` (${resp.status})` : '';
            toast.error(`${resp?.message || 'Could not clear your notifications'}${why}`);
            return;
        }

        const cleared = resp?.data?.cleared ?? unReadNotifications.length;
        toast.success(`${cleared} notification${cleared === 1 ? '' : 's'} cleared`);

        /* the parent owns the list, so it refetches and both tabs and the
           header badge settle from one response */
        if (onCleared) await onCleared();
        setClearing(false);
    };

    if (loading) return <NotificationSkeleton />;

    if (unReadNotifications.length === 0) {
        return <NoData
            title="You are all caught up"
            message="Nothing new to read. Anything that needs your attention will show up here." />;
    }

    return (
        <div className="unread_notifications">

            <div className="unread-actions">
                <span className="unread-count">
                    {unReadNotifications.length} unread
                </span>

                <Tooltip arrow placement="left"
                    title="Marks them all as read. They stay in the All tab.">
                    <button type="button" className="unread-clear"
                        disabled={clearing} onClick={clearAll}>
                        <DoneAllRoundedIcon />
                        {clearing ? 'Clearing…' : 'Clear all'}
                    </button>
                </Tooltip>
            </div>

            {unReadNotifications.map((eachItem, index) => (
                <button type="button" key={index}
                    className="notification_space_unread is-unread"
                    onClick={() => { readNotificationUpdate(eachItem); }}>
                    <div className="notif-row">
                        <div className="notif-icon">{eachItem.icon}</div>

                        <div className="notif-body">
                            <div className="notif-head">
                                <h6>{eachItem.notification.title}</h6>
                                <span className="notification_date"
                                    title={moment(eachItem.createdDate).format('DD MMM YYYY, h:mm A')}>
                                    {moment(eachItem.createdDate).format('LT')}
                                </span>
                            </div>
                            <p className="body_notifications" title={eachItem.notification.body}>
                                {eachItem.notification.body}
                            </p>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    )
}

export default UnreadNotifications